import * as d3 from 'd3'
import { motion } from 'motion/react'
import * as React from 'react'

import { cn } from '@/utils'
import { CHART_COLORS } from './Constants'
import { ChartControls } from './Controls'
import { useChartBase } from './Hooks'
import { ChartTooltip } from './Tooltip'

interface RadarDataPoint {
  category: string
  value: number
}

interface RadarSeries {
  label: string
  color?: string
  data: RadarDataPoint[]
}

interface RadarChartProps extends React.HTMLAttributes<HTMLDivElement> {
  data?: RadarDataPoint[] | RadarSeries[]
  width?: string | number
  height?: number
  margin?: number
  color?: string
  strokeWidth?: number
  fillOpacity?: number
  projectName?: string
  title?: string
  filters?: any
  schema?: any
  dateRange?: any
  filename?: string
  showHoverControls?: boolean
}

interface ExtendedRadarDataPoint extends RadarDataPoint {
  seriesLabel: string
  seriesColor: string
  x: number
  y: number
  angle: number
  dist: number
}

const RadarChart = React.forwardRef<HTMLDivElement, RadarChartProps>(
  (
    {
      data = [],
      className,
      width = '100%',
      height = 400,
      margin = 40,
      color = CHART_COLORS[0],
      strokeWidth = 2,
      fillOpacity = 0.2,
      projectName,
      title,
      filters,
      schema,
      dateRange,
      filename = 'chart-export.png',
      showHoverControls = false,
      ...props
    },
    ref,
  ) => {
    const {
      containerRef,
      dimensions,
      isFullscreen,
      toggleFullscreen,
      fullscreenError: error,
      fullscreenSupported: isSupported,
    } = useChartBase()
    const [hoveredData, setHoveredData] =
      React.useState<ExtendedRadarDataPoint | null>(null)

    const series = React.useMemo(() => {
      if (!data || !data.length) return []
      if (data[0] && 'data' in data[0] && Array.isArray(data[0].data)) {
        return (data as RadarSeries[]).map((d, i) => ({
          ...d,
          color: d.color || CHART_COLORS[i % CHART_COLORS.length],
        }))
      }
      return [
        {
          label: 'Value',
          color: color,
          data: data as RadarDataPoint[],
        },
      ]
    }, [data, color])

    const categories = React.useMemo(() => {
      if (!series.length || !series[0].data) return []
      return series[0].data.map((d: RadarDataPoint) => d.category)
    }, [series])

    const radius = React.useMemo(() => {
      if (!dimensions.width || !dimensions.height) return 0
      return Math.max(
        0,
        Math.min(dimensions.width, dimensions.height) / 2 - margin,
      )
    }, [dimensions, margin])

    const center = React.useMemo(() => {
      if (!dimensions.width || !dimensions.height) return { x: 0, y: 0 }
      return { x: dimensions.width / 2, y: dimensions.height / 2 }
    }, [dimensions])

    const { angleScale, radiusScale, pathGenerator, allPoints } =
      React.useMemo(() => {
        if (!radius || !series.length || !categories.length)
          return {
            angleScale: undefined,
            radiusScale: undefined,
            pathGenerator: undefined,
            allPoints: [] as ExtendedRadarDataPoint[],
          }

        const angle = d3
          .scaleBand()
          .domain(categories)
          .range([0, 2 * Math.PI])
          .align(0)

        const maxValue =
          d3.max(
            series.flatMap((s: RadarSeries) => s.data),
            (d: RadarDataPoint) => d.value,
          ) || 0

        const r = d3
          .scaleLinear()
          .domain([0, maxValue])
          .nice()
          .range([0, radius])

        const line = d3
          .lineRadial<RadarDataPoint>()
          .angle((d: RadarDataPoint) => {
            const a = angle(d.category)
            return a === undefined ? 0 : a
          })
          .radius((d: RadarDataPoint) => r(d.value))
          .curve(d3.curveLinearClosed)

        const pts: ExtendedRadarDataPoint[] = series.flatMap((s: RadarSeries) =>
          s.data.map((d: RadarDataPoint) => {
            const a = (angle(d.category) || 0) - Math.PI / 2
            const dist = r(d.value)
            return {
              ...d,
              seriesLabel: s.label,
              seriesColor: s.color || CHART_COLORS[0],
              x: center.x + dist * Math.cos(a),
              y: center.y + dist * Math.sin(a),
              angle: a,
              dist,
            }
          }),
        )

        return {
          angleScale: angle,
          radiusScale: r,
          pathGenerator: line,
          allPoints: pts,
        }
      }, [series, categories, radius, center])

    const handlePointerMove = (point: ExtendedRadarDataPoint) => {
      setHoveredData(point)
    }

    const handlePointerLeave = () => {
      setHoveredData(null)
    }

    React.useImperativeHandle(ref, () => containerRef.current!, [])

    if (!series.length) return null

    return (
      <div
        ref={containerRef}
        className={cn('w-full h-full relative group', className)}
        {...props}
      >
        {radius > 0 && angleScale && radiusScale && (
          <svg
            role="img"
            aria-label="Radar chart"
            width={dimensions.width}
            height={dimensions.height}
            viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
            className="overflow-visible"
          >
            <g transform={`translate(${center.x},${center.y})`}>
              {radiusScale
                .ticks(5)
                .slice(1)
                .map((t: number, i: number) => (
                  <g key={i}>
                    <circle
                      r={radiusScale(t)}
                      fill="none"
                      stroke="currentColor"
                      strokeOpacity={0.1}
                    />
                    <text
                      y={-radiusScale(t)}
                      dy="0.35em"
                      fill="currentColor"
                      fontSize={isFullscreen ? '14' : '10'}
                      opacity={0.5}
                      textAnchor="middle"
                      className="bg-background select-none"
                      style={{ textShadow: '0 0 4px var(--background)' }}
                    >
                      {t}
                    </text>
                  </g>
                ))}

              {categories.map((cat: string, i: number) => {
                const angle = (angleScale(cat) || 0) - Math.PI / 2
                return (
                  <line
                    key={i}
                    x1={0}
                    y1={0}
                    x2={radius * Math.cos(angle)}
                    y2={radius * Math.sin(angle)}
                    stroke="currentColor"
                    strokeOpacity={0.1}
                  />
                )
              })}

              {categories.map((cat: string, i: number) => {
                const angle = (angleScale(cat) || 0) - Math.PI / 2
                const labelRadius = radius + 20
                const x = labelRadius * Math.cos(angle)
                const y = labelRadius * Math.sin(angle)

                return (
                  <text
                    key={i}
                    x={x}
                    y={y}
                    dy="0.35em"
                    textAnchor={
                      Math.abs(x) < 5 ? 'middle' : x > 0 ? 'start' : 'end'
                    }
                    fill="currentColor"
                    fontSize={isFullscreen ? '15' : '11'}
                    className="font-medium select-none"
                  >
                    {cat}
                  </text>
                )
              })}

              {series.map((s: RadarSeries, i: number) => (
                <motion.path
                  key={i}
                  d={pathGenerator?.(s.data) || undefined}
                  fill={s.color}
                  fillOpacity={fillOpacity}
                  stroke={s.color}
                  strokeWidth={strokeWidth}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                />
              ))}

              {allPoints.map((p: ExtendedRadarDataPoint, i: number) => {
                const isHovered =
                  hoveredData &&
                  hoveredData.seriesLabel === p.seriesLabel &&
                  hoveredData.category === p.category
                return (
                  <g key={i}>
                    <circle
                      cx={p.x - center.x}
                      cy={p.y - center.y}
                      r={10}
                      fill="transparent"
                      onPointerEnter={() => handlePointerMove(p)}
                      onPointerLeave={handlePointerLeave}
                      className="cursor-pointer"
                    />
                    <circle
                      cx={p.x - center.x}
                      cy={p.y - center.y}
                      r={4}
                      fill={isHovered ? p.seriesColor : 'var(--color-card)'}
                      stroke={p.seriesColor}
                      strokeWidth={2}
                      className="pointer-events-none transition-colors duration-200"
                    />
                  </g>
                )
              })}
            </g>
          </svg>
        )}

        {showHoverControls && (
          <ChartControls
            containerRef={containerRef}
            isFullscreen={isFullscreen}
            toggleFullscreen={toggleFullscreen}
            fullscreenError={error}
            fullscreenSupported={isSupported}
            filename={filename}
            projectName={projectName}
            title={title}
            filters={filters}
            schema={schema}
            dateRange={dateRange}
          />
        )}

        <ChartTooltip
          active={!!hoveredData}
          position={
            hoveredData ? { x: hoveredData.x, y: hoveredData.y } : undefined
          }
        >
          {hoveredData && (
            <>
              <div className="font-semibold">{hoveredData.value}</div>
              <div className="text-muted-foreground opacity-80 text-xs uppercase tracking-wider mb-1">
                {hoveredData.seriesLabel}
              </div>
              <div className="text-muted-foreground opacity-80">
                {hoveredData.category}
              </div>
            </>
          )}
        </ChartTooltip>
      </div>
    )
  },
)
RadarChart.displayName = 'RadarChart'

export { RadarChart }
